import { Star } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";

interface Review {
  author_name: string;
  rating: number;
  text: string;
  location: string;
}

const Reviews = () => {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  // Reviews destacados de pacientes
  const reviews: Review[] = [
    {
      author_name: "Sarah M.",
      rating: 5,
      text: t('reviews.review1.comment'),
      location: t('reviews.review1.location'),
    },
    {
      author_name: "Michael R.",
      rating: 5,
      text: t('reviews.review2.comment'),
      location: t('reviews.review2.location'),
    },
    {
      author_name: "Jennifer L.",
      rating: 5,
      text: t('reviews.review3.comment'),
      location: t('reviews.review3.location'),
    },
  ];

  const moveTo = (index: number) => {
    setCurrentIndex(index);
  };

  // Auto-rotation effect
  useEffect(() => {
    if (isPaused) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % reviews.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isPaused, reviews.length]);

  // URL de Google reviews
  const googleUrl = "https://www.google.com/maps/search/LS+Dental+Clinic+Playas+del+Coco+Costa+Rica";

  return (
    <section
      id="reviews"
      className="py-16 sm:py-20 md:py-32 bg-secondary/20"
      role="region"
      aria-label="Patient reviews and testimonials"
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 sm:mb-14 md:mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-4 sm:mb-6 px-2">
            {t('reviews.title')}
          </h2>
          
          <div className="flex flex-col md:flex-row items-center justify-center gap-3 sm:gap-4 mb-4">
            <div className="flex items-center gap-2">
              <div className="flex gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 sm:w-6 h-5 sm:h-6 fill-primary text-primary" />
                ))}
              </div>
              <span className="text-lg sm:text-xl font-semibold text-foreground">5.0</span>
            </div>
            <a
              href={googleUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg bg-card hover:bg-muted/50 transition-colors border border-border"
              aria-label="View LS Dental reviews on Google Maps"
            >
              <svg
                viewBox="0 0 48 48"
                className="w-5 sm:w-6 h-5 sm:h-6"
                aria-hidden="true"
                role="img"
              >
                <path fill="#4285F4" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#34A853" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill="#EA4335" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              </svg>
              <span className="text-xs sm:text-sm font-medium text-foreground">{t('reviews.viewOnGoogle')}</span>
            </a>
          </div>
          
          <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto px-4">
            {t('reviews.subtitle')}
          </p>
        </div>

        {/* Carousel */}
        <div className="max-w-2xl mx-auto">
          <div 
            className="relative overflow-hidden rounded-[20px] bg-card shadow-lg"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            <div
              className="flex transition-transform duration-600 ease-in-out"
              style={{ transform: `translateX(-${currentIndex * 100}%)` }}
            >
              {reviews.map((review, index) => (
                <div
                  key={index}
                  className="min-w-full p-6 sm:p-8 md:p-10"
                >
                  <div className="mb-4">
                    <h3 className="font-semibold text-lg sm:text-xl text-foreground mb-3">
                      {review.author_name}
                    </h3>
                    <div className="flex gap-1 mb-4">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={`w-4 sm:w-5 h-4 sm:h-5 ${
                            i < review.rating
                              ? "fill-[#f4b400] text-[#f4b400]"
                              : "text-muted"
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                  <p className="text-sm sm:text-base leading-relaxed text-foreground/90">
                    {review.text}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Dots */}
          <div className="flex justify-center gap-2 mt-6">
            {reviews.map((_, index) => (
              <button
                key={index}
                onClick={() => moveTo(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  index === currentIndex
                    ? "bg-primary w-8"
                    : "bg-muted hover:bg-muted-foreground/50"
                }`}
                aria-label={`Go to review ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Reviews;
